import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import Input from "../../../components/input";
import Button from "../../../components/button";
import ToastComponent from "../../../components/toast";
import { AppDispatch } from "../../../redux/store";
import {
  ChangePasswordRequest,
  changePasswordStateAction,
  clearUserStates,
  getChangePasswordState,
} from "../../../redux/features/user-slice";
import {
  getErrorState,
  setErrorState,
} from "../../../redux/features/common-slice";
import { setLogout } from "../../../redux/features/auth-slice";
import { clearTaskStates } from "../../../redux/features/task-slice";
import { clearOtherStates } from "../../../redux/features/other-slice";
import paths from "../../../navigation/paths";

export default function ChangePasswordPage() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const changePasswordState = useSelector(getChangePasswordState);
  const errorState = useSelector(getErrorState);

  const handleChange = async (values: ChangePasswordRequest) => {
    await dispatch(
      changePasswordStateAction({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
    );
  };

  useEffect(() => {
    if (changePasswordState.data && changePasswordState.data.success) {
      toast(changePasswordState.data.message, { type: "success" });
      setTimeout(() => {
        dispatch(clearUserStates());
        dispatch(setLogout());
        dispatch(clearTaskStates());
        dispatch(clearOtherStates());
        dispatch(setErrorState(null));
        navigate(paths.LOGIN, { replace: true });
      }, 2000);
    }
  }, [changePasswordState]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <>
      <Helmet title="Change Password" />
      <section
        className="h-screen content-center bg-background-default"
        style={{
          height: "calc(100vh - 32px)",
        }}
      >
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <div className="w-full md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 space-y-8 sm:p-8 rounded-2xl shadow-lg bg-background-paper">
              <div className="flex items-center">
                <img className="h-12" src="/logo.png" alt="logo" />
              </div>
              <Formik
                initialValues={{
                  currentPassword: "",
                  newPassword: "",
                }}
                validationSchema={Yup.object().shape({
                  currentPassword: Yup.string()
                    .min(8, "Must be at least 8 characters")
                    .max(20, "Must be at most 20 characters")
                    .matches(
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]).{8,}$/,
                      "Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)"
                    )
                    .required("Current Password is required"),
                  newPassword: Yup.string()
                    .min(8, "Must be at least 8 characters")
                    .max(20, "Must be at most 20 characters")
                    .matches(
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]).{8,}$/,
                      "Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)"
                    )
                    .required("New Password is required"),
                })}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                  handleChange(values);
                }}
              >
                {({
                  values,
                  errors,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <form
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit}
                    className="space-y-4 md:space-y-6"
                  >
                    <Input
                      label="Current Password"
                      type="password"
                      id="currentPassword"
                      value={values.currentPassword}
                      onChange={handleChange}
                      errorText={errors.currentPassword}
                    />
                    <Input
                      label="New Password"
                      type="password"
                      id="newPassword"
                      value={values.newPassword}
                      onChange={handleChange}
                      errorText={errors.newPassword}
                    />
                    <Button
                      label="Change Password"
                      type="submit"
                      loading={changePasswordState.loading}
                      disabled={
                        Array.isArray(errors) ||
                        Object.values(errors).toString() != "" ||
                        changePasswordState.loading
                      }
                      exClassName="w-full"
                    />
                    <p
                      className="w-full text-sm text-center cursor-pointer"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </p>
                  </form>
                )}
              </Formik>
            </div>
          </div>
        </div>
        <ToastComponent id="changePassword" />
      </section>
    </>
  );
}
